/**
 * Contrôles de filtre partagés entre la page Apparts for Rent V1
 * (src/pages/apparts-for-rent/AppartsForRentPage.tsx) et V2
 * (src/v2/pages/AppartsForRentPage.tsx).
 *
 * Extraits de la V1 le 2026-06-01 pour permettre la parité des filtres en V2
 * sans dupliquer le code. Composants purs (aucune dépendance au type Bien) :
 *  - Toggle           : interrupteur on/off (meublé, company…)
 *  - CheckDropdown    : multi-sélection (région, chambres…)
 *  - SingleDropdown   : sélection unique (transaction…)
 *  - PostalCodeFilter : chips de codes postaux avec lookup commune
 */
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Check, X, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { getCityFromPostalCode, getPostalCodeFromCity } from "@/lib/postalCodeLookup";

// ─── Toggle button ────────────────────────────────────────────────────────────

export function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (!disabled) onChange(!checked);
      }}
      className={cn(
        "flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors",
        disabled
          ? "border-dashed border-border text-text-muted bg-surface opacity-50 cursor-not-allowed"
          : checked
            ? "border-accent text-accent bg-accent/5 cursor-pointer"
            : "border-dashed border-border text-text-secondary hover:text-text-primary bg-surface cursor-pointer",
      )}
    >
      <span
        className={cn(
          "flex items-center justify-center w-4 h-4 rounded border transition-colors flex-shrink-0",
          checked ? "bg-accent border-accent" : "border-border bg-surface",
        )}
      >
        {checked && <Check size={10} strokeWidth={3} className="text-white" />}
      </span>
      {label}
    </button>
  );
}

// ─── Check dropdown (multi-select) ────────────────────────────────────────────

export function CheckDropdown({
  label,
  options,
  selected,
  onChange,
  disabled,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: Set<string>;
  onChange: (v: Set<string>) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(next);
  };
  const allSelected = selected.size === options.length;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
        className={cn(
          "flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border transition-colors",
          disabled
            ? "border-dashed border-border text-text-muted bg-surface opacity-50 cursor-not-allowed"
            : open
              ? "border-accent text-accent bg-surface cursor-pointer"
              : "border-dashed border-border text-text-secondary hover:text-text-primary bg-surface cursor-pointer",
        )}
      >
        <span>{label}</span>
        {!disabled && !allSelected && (
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-accent text-white text-[10px] font-medium">
            {selected.size}
          </span>
        )}
        <ChevronDown size={13} className={cn("transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-surface border border-border rounded-lg shadow-lg py-1 min-w-[160px]">
          {options.map((opt) => {
            const checked = selected.has(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-text-secondary hover:bg-surface-2 hover:text-text-primary transition-colors cursor-pointer"
              >
                <span
                  className={cn(
                    "flex items-center justify-center w-4 h-4 rounded border flex-shrink-0 transition-colors",
                    checked ? "bg-accent border-accent" : "border-border bg-surface",
                  )}
                >
                  {checked && <Check size={10} strokeWidth={3} className="text-white" />}
                </span>
                <span>{opt.label}</span>
              </button>
            );
          })}
          <div className="border-t border-border mt-1 pt-1 flex gap-1 px-2 pb-1">
            <button
              type="button"
              onClick={() => onChange(new Set(options.map((o) => o.value)))}
              className="flex-1 text-[11px] text-text-muted hover:text-accent py-1 cursor-pointer"
            >
              {t("common.selectAll")}
            </button>
            <span className="text-border self-center">|</span>
            <button
              type="button"
              onClick={() => onChange(new Set())}
              className="flex-1 text-[11px] text-text-muted hover:text-accent py-1 cursor-pointer"
            >
              {t("common.none")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Single-value dropdown (utilisé pour le filtre transaction) ───────────────

export function SingleDropdown<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const current = options.find((o) => o.value === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
        className={cn(
          "flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border transition-colors",
          disabled
            ? "border-dashed border-border text-text-muted bg-surface opacity-50 cursor-not-allowed"
            : open
              ? "border-accent text-accent bg-surface cursor-pointer"
              : "border-dashed border-border text-text-secondary hover:text-text-primary bg-surface cursor-pointer",
        )}
      >
        <span className="text-text-muted">{label}:</span>
        <span className="font-medium">{current?.label ?? "—"}</span>
        <ChevronDown size={13} className={cn("transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-surface border border-border rounded-lg shadow-lg py-1 min-w-[160px]">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={cn(
                "w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors cursor-pointer",
                opt.value === value
                  ? "text-accent font-medium bg-accent/5"
                  : "text-text-secondary hover:bg-surface-2 hover:text-text-primary",
              )}
            >
              {opt.value === value && <Check size={12} strokeWidth={3} className="text-accent" />}
              <span className={opt.value === value ? "" : "ml-[18px]"}>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Postal code multi-filter ────────────────────────────────────────────────

export function PostalCodeFilter({
  codes,
  onChange,
  disabled,
}: {
  codes: string[];
  onChange: (v: string[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  // Feedback inline : "code inconnu", "déjà ajouté", "format invalide". Avant 2026-05-08
  // l'input était vidé silencieusement quand le code n'existait pas → l'utilisateur
  // pensait que le module était cassé. On garde la valeur saisie + affiche un message.
  const [error, setError] = useState<string | null>(null);

  const tryAdd = (raw: string) => {
    const val = raw.trim();
    if (!val) return;

    // Cas 1 : 4 chiffres — lookup direct.
    if (/^\d{4}$/.test(val)) {
      const city = getCityFromPostalCode(val);
      if (!city) {
        setError(t("appartsForRent.codePostalErrorNotFound"));
        return;
      }
      if (codes.includes(val)) {
        setError(t("appartsForRent.codePostalErrorDup"));
        return;
      }
      onChange([...codes, val]);
      setInputValue("");
      setError(null);
      return;
    }

    // Cas 2 : nom de commune — reverse lookup. ≥ 2 caractères, lettres + accents.
    if (val.length >= 2 && /^[A-Za-zÀ-ÿ\s'-]+$/.test(val)) {
      const code = getPostalCodeFromCity(val);
      if (!code) {
        setError(t("appartsForRent.codePostalErrorNotFound"));
        return;
      }
      if (codes.includes(code)) {
        setError(t("appartsForRent.codePostalErrorDup"));
        return;
      }
      onChange([...codes, code]);
      setInputValue("");
      setError(null);
      return;
    }

    // Cas 3 : ni 4 chiffres ni texte exploitable.
    setError(t("appartsForRent.codePostalErrorFormat"));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    tryAdd(inputValue);
  };

  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (error) setError(null);
  };

  const remove = (code: string) => onChange(codes.filter((c) => c !== code));

  return (
    <div className={cn("flex flex-col gap-1", disabled && "opacity-50 pointer-events-none")}>
      <div className="flex items-center gap-1.5 flex-wrap">
        {codes.map((code) => {
          const city = getCityFromPostalCode(code);
          return (
            <span
              key={code}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-accent text-accent bg-accent/5"
            >
              <MapPin size={10} />
              {code} — {city}
              <button type="button" onClick={() => remove(code)} className="ml-0.5 hover:text-danger cursor-pointer">
                <X size={10} />
              </button>
            </span>
          );
        })}
        <input
          disabled={disabled}
          className={cn(
            "px-3 py-2 text-sm bg-surface border border-dashed rounded-md text-text-primary placeholder:text-text-muted outline-none w-[240px]",
            error ? "border-danger" : "border-border",
            disabled && "cursor-not-allowed",
          )}
          placeholder={t("appartsForRent.codePostalPlaceholder")}
          value={inputValue}
          onChange={onInput}
          onKeyDown={handleKeyDown}
        />
      </div>
      {error && <span className="text-xs text-danger pl-1">{error}</span>}
    </div>
  );
}
